import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, ChevronDown, LogOut, Crown, CreditCard } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { color, alpha, radius, shadow } from '../../config/tokens';

const TIER_LABELS = { free: 'Free', pro: 'Pro', team: 'Team' };
const TIER_COLORS = { free: color.text.subtle, pro: color.accent.primary, team: color.status.warning };

export const UserMenu = () => {
  const { user, handleSignOut, subscription } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls="user-menu"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 1rem',
          backgroundColor: alpha(color.accent.primary, 0.1),
          border: `1px solid ${alpha(color.accent.primary, 0.3)}`,
          borderRadius: radius.md,
          color: color.accent.light,
          cursor: 'pointer',
          fontSize: '0.875rem'
        }}
      >
        <User size={16} aria-hidden="true" />
        <span>{user.displayName || user.email?.split('@')[0]}</span>
        <ChevronDown size={14} aria-hidden="true" style={{
          transition: 'transform 0.2s',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
        }} />
      </button>

      {isOpen && (
        <div
          id="user-menu"
          role="menu"
          aria-label="User account menu"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '0.5rem',
            backgroundColor: color.bg.surface,
            border: `1px solid ${color.border.default}`,
            borderRadius: radius.md,
            minWidth: '200px',
            boxShadow: shadow.md,
            zIndex: 100
          }}
        >
          <div style={{
            padding: '0.75rem 1rem',
            borderBottom: `1px solid ${color.border.default}`
          }}>
            <p style={{ margin: 0, color: color.text.primary, fontSize: '0.875rem', fontWeight: 500 }}>
              {user.displayName || user.email?.split('@')[0]}
            </p>
            <p style={{ margin: '0.25rem 0 0 0', color: color.text.subtle, fontSize: '0.75rem' }}>
              {user.email}
            </p>
            {subscription && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                marginTop: '0.375rem',
                padding: '0.125rem 0.5rem',
                backgroundColor: alpha(TIER_COLORS[subscription.tier] || TIER_COLORS.free, 0.125),
                borderRadius: radius.xs,
                fontSize: '0.6875rem',
                color: TIER_COLORS[subscription.tier] || TIER_COLORS.free,
                fontWeight: 500
              }}>
                <Crown size={10} />
                {TIER_LABELS[subscription.tier] || 'Free'}
              </div>
            )}
          </div>
          <button
            role="menuitem"
            onClick={() => {
              navigate('/account');
              setIsOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              width: '100%',
              padding: '0.75rem 1rem',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: `1px solid ${color.border.default}`,
              color: color.text.secondary,
              cursor: 'pointer',
              fontSize: '0.875rem',
              textAlign: 'left'
            }}
          >
            <CreditCard size={16} aria-hidden="true" />
            Account &amp; billing
          </button>
          <button
            role="menuitem"
            onClick={() => {
              handleSignOut();
              setIsOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              width: '100%',
              padding: '0.75rem 1rem',
              backgroundColor: 'transparent',
              border: 'none',
              color: color.status.danger,
              cursor: 'pointer',
              fontSize: '0.875rem',
              textAlign: 'left'
            }}
          >
            <LogOut size={16} aria-hidden="true" />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
